/**
 * MasterWEB Market R8.34.1 — category directory runtime.
 *
 * Engineering goals:
 * - Keep only five collection rows active per directory page.
 * - Keep products 7+ of each row inert in <template> until customers approach them.
 * - Use one delegated click/keyboard handler for the whole directory.
 * - Batch scroll-state updates in requestAnimationFrame.
 * - Cleanly disconnect observers/listeners when Shopify replaces the section.
 */

const reducedMotionMedia = window.matchMedia?.('(prefers-reduced-motion: reduce)');
const prefersReducedMotion = () => reducedMotionMedia?.matches ?? false;
const RAIL_TOLERANCE = 3;

class MWProductDirectory extends HTMLElement {
  connectedCallback() {
    if (this.dataset.mwReady === 'true') return;
    this.dataset.mwReady = 'true';

    this.viewport = this.querySelector('[data-mw-directory-viewport]');
    this.pagination = this.querySelector('[data-mw-directory-pagination]');
    this.announcement = this.querySelector('[data-mw-page-announcement]');
    this.templates = new Map(
      [...this.querySelectorAll('template[data-mw-category-page-template]')].map((template) => [
        Number(template.dataset.mwCategoryPageTemplate),
        template,
      ])
    );

    this.pageCount = Number(this.dataset.mwPageCount || this.templates.size || 1);
    this.currentPage = 1;
    this.controller = new AbortController();
    this.pageController = null;
    this.resizeObserver = null;

    const { signal } = this.controller;
    this.addEventListener('click', (event) => this.#handleClick(event), { signal });
    this.addEventListener('keydown', (event) => this.#handleKeydown(event), { signal });

    if (this.templates.size > 0 && this.viewport) {
      this.#renderPage(1, { announce: false });
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.pageController?.abort();
    this.resizeObserver?.disconnect();
    delete this.dataset.mwReady;
  }

  #handleClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const railButton = target.closest('[data-mw-rail-prev], [data-mw-rail-next]');
    if (railButton) {
      const row = railButton.closest('[data-mw-category-row]');
      const rail = row?.querySelector('[data-mw-product-rail]');
      if (!row || !rail) return;
      const direction = railButton.hasAttribute('data-mw-rail-prev') ? -1 : 1;
      this.#advanceRail(row, rail, direction);
      return;
    }

    const pageButton = target.closest('[data-mw-page-number], [data-mw-page-action]');
    if (!pageButton) return;

    let nextPage = this.currentPage;
    if (pageButton.hasAttribute('data-mw-page-number')) {
      nextPage = Number(pageButton.dataset.mwPageNumber);
    } else {
      switch (pageButton.dataset.mwPageAction) {
        case 'first': nextPage = 1; break;
        case 'prev': nextPage = this.currentPage - 1; break;
        case 'next': nextPage = this.currentPage + 1; break;
        case 'last': nextPage = this.pageCount; break;
      }
    }

    nextPage = Math.min(this.pageCount, Math.max(1, nextPage));
    if (nextPage !== this.currentPage) this.#renderPage(nextPage, { announce: true });
  }

  #handleKeydown(event) {
    const rail = event.target instanceof Element ? event.target.closest('[data-mw-product-rail]') : null;
    if (!rail || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    const row = rail.closest('[data-mw-category-row]');
    if (!row) return;

    event.preventDefault();
    this.#advanceRail(row, rail, event.key === 'ArrowLeft' ? -1 : 1);
  }

  #advanceRail(row, rail, direction) {
    const shouldMaterialize = direction > 0 && this.#shouldMaterializeDeferred(row, rail);
    if (shouldMaterialize) this.#materializeDeferred(row, rail);

    const scroll = () => {
      rail.scrollBy({
        left: Math.max(rail.clientWidth, 1) * direction,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
      this.#scheduleRailUpdate(row);
    };

    // After inserting deferred cards, wait one frame so scrollWidth is current.
    if (shouldMaterialize) requestAnimationFrame(scroll);
    else scroll();
  }

  #shouldMaterializeDeferred(row, rail) {
    if (!row.querySelector('template[data-mw-rail-deferred]')) return false;
    const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
    if (maxScroll <= RAIL_TOLERANCE) return true;

    // Hydrate shortly before the customer reaches the end. This keeps scrolling
    // continuous while avoiding early initialization of the second product group.
    return rail.scrollLeft + (rail.clientWidth * 1.5) >= maxScroll;
  }

  #materializeDeferred(row, rail) {
    const template = row.querySelector('template[data-mw-rail-deferred]');
    if (!(template instanceof HTMLTemplateElement)) return false;

    rail.append(template.content.cloneNode(true));
    template.remove();
    row.dataset.mwRailHydrated = 'true';
    this.#scheduleRailUpdate(row);
    return true;
  }

  #renderPage(page, { announce = true } = {}) {
    const template = this.templates.get(page);
    if (!template || !this.viewport) return;

    this.pageController?.abort();
    this.resizeObserver?.disconnect();
    this.pageController = new AbortController();

    this.viewport.setAttribute('aria-busy', 'true');
    this.viewport.replaceChildren(template.content.cloneNode(true));
    this.viewport.removeAttribute('aria-busy');

    this.currentPage = page;
    this.dataset.mwCurrentPage = String(page);

    this.#bindActiveRails();
    this.#updatePagination();

    if (announce) {
      if (this.announcement) this.announcement.textContent = `Product categories page ${page} of ${this.pageCount}`;
      const heading = this.querySelector('.mw-section-heading--directory');
      heading?.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }
  }

  #bindActiveRails() {
    const { signal } = this.pageController;
    const rows = [...this.viewport.querySelectorAll('[data-mw-category-row]')];

    for (const row of rows) {
      const rail = row.querySelector('[data-mw-product-rail]');
      if (!rail) continue;

      rail.addEventListener('scroll', () => {
        if (this.#shouldMaterializeDeferred(row, rail)) this.#materializeDeferred(row, rail);
        this.#scheduleRailUpdate(row);
      }, { passive: true, signal });

      requestAnimationFrame(() => this.#updateRailButtons(row));
    }

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const row = entry.target.closest?.('[data-mw-category-row]');
          if (row) this.#scheduleRailUpdate(row);
        }
      });
      for (const rail of this.viewport.querySelectorAll('[data-mw-product-rail]')) this.resizeObserver.observe(rail);
    } else {
      window.addEventListener('resize', () => rows.forEach((row) => this.#scheduleRailUpdate(row)), { passive: true, signal });
    }
  }

  #scheduleRailUpdate(row) {
    if (row.dataset.mwRailRaf === 'true') return;
    row.dataset.mwRailRaf = 'true';
    requestAnimationFrame(() => {
      delete row.dataset.mwRailRaf;
      if (row.isConnected) this.#updateRailButtons(row);
    });
  }

  #updateRailButtons(row) {
    const rail = row.querySelector('[data-mw-product-rail]');
    const prev = row.querySelector('[data-mw-rail-prev]');
    const next = row.querySelector('[data-mw-rail-next]');
    if (!rail || !prev || !next) return;

    const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const hasOverflow = maxScroll > RAIL_TOLERANCE;
    const hasDeferred = Boolean(row.querySelector('template[data-mw-rail-deferred]'));

    prev.disabled = !hasOverflow || rail.scrollLeft <= RAIL_TOLERANCE;
    next.disabled = !hasDeferred && (!hasOverflow || rail.scrollLeft >= maxScroll - RAIL_TOLERANCE);
    row.toggleAttribute('data-mw-no-overflow', !hasOverflow && !hasDeferred);
  }

  #updatePagination() {
    if (!this.pagination) return;

    for (const button of this.pagination.querySelectorAll('[data-mw-page-number]')) {
      const isCurrent = Number(button.dataset.mwPageNumber) === this.currentPage;
      if (isCurrent) button.setAttribute('aria-current', 'page');
      else button.removeAttribute('aria-current');
      button.classList.toggle('is-current', isCurrent);
    }

    const first = this.pagination.querySelector('[data-mw-page-action="first"]');
    const prev = this.pagination.querySelector('[data-mw-page-action="prev"]');
    const next = this.pagination.querySelector('[data-mw-page-action="next"]');
    const last = this.pagination.querySelector('[data-mw-page-action="last"]');

    if (first) first.disabled = this.currentPage <= 1;
    if (prev) prev.disabled = this.currentPage <= 1;
    if (next) next.disabled = this.currentPage >= this.pageCount;
    if (last) last.disabled = this.currentPage >= this.pageCount;
  }
}

if (!customElements.get('mw-product-directory')) {
  customElements.define('mw-product-directory', MWProductDirectory);
}
